/* eslint-disable react/require-default-props */

import React, { useState } from 'react';
import {
  Box,
  ButtonIcon,
  CardHeader,
  Icon,
  Menu,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import { RoleImpact } from '../../../../shared';
import { useInvertedLocalize } from '../../../../utils/localization';

import invertedMessages from './messages';

import './RoleCardHeaderCompact.css';

type ImpactLevel = 'low' | 'medium' | 'high';

interface RoleCardHeaderCompactProps {
  userCount: number; // num users matched by this role
  impact?: ImpactLevel;
  onIgnoreClick: () => void;
  isIgnoreButtonDisabled?: boolean;
}

/**
 * RoleCardHeaderCompact component displaying the top section of a compact role card
 * with security icon, user count, impact badge, and kebab menu
 *
 * @param props - The component props
 * @returns JSX element containing the compact role card header
 */
function RoleCardHeaderCompact({
  userCount,
  impact,
  onIgnoreClick,
  isIgnoreButtonDisabled = false,
}: RoleCardHeaderCompactProps) {
  const l = useInvertedLocalize(invertedMessages);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleIgnoreMenuClick = () => {
    setIsMenuOpen(false);
    onIgnoreClick();
  };

  const menuOptions = [
    {
      label: 'Ignore',
      value: 'ignore',
      isDisabled: isIgnoreButtonDisabled,
    },
  ];

  return (
    <CardHeader className='Roles-RoleCardHeaderCompact'>
      <Box className='Roles-RoleCardHeaderCompact-content'>
        {/* Icon, user count, and impact */}
        <Box className='Roles-RoleCardHeaderCompact-info'>
          <Icon
            kind='Security'
            size='medium'
            color='--color-foreground-neutral-bold'
          />
          <Typography
            kind='h3'
            color='--color-foreground-neutral-bold'
            className='Roles-RoleCardHeaderCompact-userCount'>
            {l(
              '{count, plural, one {# Included User} other {# Included Users}}',
              { count: userCount }
            )}
          </Typography>
          {impact && (
            <RoleImpact
              impactLevel={impact}
              iconSize='small'
              className='Roles-RoleCardHeaderCompact-impact'
            />
          )}
        </Box>

        {/* Kebab menu */}
        <Box className='Roles-RoleCardHeaderCompact-actions'>
          <Tooltip
            text={
              isIgnoreButtonDisabled
                ? l(
                    "You don't have the permissions to ignore or the feature is turned off."
                  )
                : ''
            }
            isDisabled={!isIgnoreButtonDisabled}
            placement='top'>
            <Menu
              kind='simple'
              options={menuOptions}
              isMenuExpanded={isMenuOpen}
              setIsExpanded={setIsMenuOpen}
              onChange={handleIgnoreMenuClick}
              trigger={
                <ButtonIcon
                  kind='ghost'
                  size='medium'
                  isDisabled={isIgnoreButtonDisabled}
                  ariaLabel='More options'
                  icon={<Icon kind='MoreVertical' size='medium' />}
                  className='Roles-RoleCardHeaderCompact-kebabButton'
                />
              }
            />
          </Tooltip>
        </Box>
      </Box>
    </CardHeader>
  );
}

export default RoleCardHeaderCompact;
